import useLoader from "@client/components/utils/useLoader";
import { trpc } from "@client/trpc";
import { exportSpeculationSettlements } from "@client/utils/exportSpeculationSettlements";
import {
  assertGetFunder,
  getActiveFunders,
  getActiveProposalForms,
  getActiveRecommenders,
  getEvaluatorActiveEvaluationIds,
  getEvaluatorTrancheList,
} from "@engine/round/accessors";
import {
  Funder,
  Proposal,
  ProposalForm,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import { getEvaluableIndicesMap } from "@lib/algoInterface/getEvaluableIndicesMap";
import { trancheListToAlgoTranches } from "@lib/algoInterface/trancheListToAlgoInterface";
import { Tranche, weightedUtility } from "@lib/specGrantSettlementAlgo";
import { UserProfile } from "@lib/types";
import keyBy from "lodash/keyBy";
import sum from "lodash/sum";
import { useContext, useEffect, useState } from "react";
import { StateContext } from "./Round";

export type GrantRow = {
  utility?: number;
  [key: string]: string | number | undefined;
};

export type MultiGrant = {
  specGrantId: string;
  timestamp: string;
  grants: GrantRow[];
};

export type SpeculationGrantApplicationData = {
  application: Proposal;
  simultaneousGrants: MultiGrant[];
  request: {
    [key: string]: string;
  };
};

export type ApplicationSpeculationGrantData = {
  [sffApplicationId: string]: SpeculationGrantApplicationData;
};

const getSpeculationGrantData = () => trpc().getSpeculationGrantData.query();
const getGrantRequestData = () => trpc().getGrantRequestData.query();

export type SpeculationGrantData = {
  applicationSpeculationGrantData: ApplicationSpeculationGrantData;
  totalGrantAmount: number;
  settlementBudget: number;
  funderId: string;
  speculationSettlements: string;
};

export type SpeculationGrantLoader =
  | {
      type: "success";
      data: SpeculationGrantData;
    }
  | {
      type: "loading";
    }
  | {
      type: "error";
      error: Error;
    }
  | {
      type: "noFunder";
    };

export function useSpeculationGrantData(): SpeculationGrantLoader {
  const { round, userProfiles } = useContext(StateContext);
  const [result, setResult] = useState<SpeculationGrantLoader>({
    type: "loading",
  });

  const grantData = useLoader(getSpeculationGrantData);
  const requestData = useLoader(getGrantRequestData);

  const users = Object.values(userProfiles);

  // load data and calculate weighted utility
  useEffect(() => {
    if (grantData.type === "error") {
      setResult({ type: "error", error: grantData.error });
      return;
    }

    if (requestData.type === "error") {
      setResult({ type: "error", error: requestData.error });
      return;
    }

    if (grantData.type === "loading" || requestData.type === "loading") {
      return;
    }

    const grantsAndApplications = processGrantData(
      grantData.data,
      requestData.data,
      users,
      round,
    );

    const totalGrantAmount = calculateTotalGrantAmount(grantsAndApplications);

    const funder = findSettlementFunder(round);
    if (!funder) {
      setResult({ type: "noFunder" });
      return;
    }

    const settlementBudget =
      funder.speculationGrantSettlementBudget || totalGrantAmount;

    const initialData = {
      applicationSpeculationGrantData: grantsAndApplications,
      totalGrantAmount,
      settlementBudget,
      funderId: funder.id,
    };
    const speculationSettlements = exportSpeculationSettlements(initialData);

    setResult({
      type: "success",
      data: { ...initialData, speculationSettlements },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grantData, requestData]);

  // get new settlement budget
  useEffect(() => {
    const funder = findSettlementFunder(round);
    if (!funder) {
      setResult({ type: "noFunder" });
      return;
    }

    setResult((prev) => {
      if (prev.type !== "success") {
        return prev;
      }

      const funder = assertGetFunder(round, prev.data.funderId);

      const settlementBudget =
        funder.speculationGrantSettlementBudget || prev.data.totalGrantAmount;

      return {
        ...prev,
        settlementBudget,
      };
    });
  }, [round]);

  return result;
}

function addNormalizedWeightedUtilitiesToSpeculationGrantData(
  round: RoundAggregate,
  users: UserProfile[],
  grantData: ApplicationSpeculationGrantData,
): void /* We modify grantData rather than returning a value */ {
  const recommenders = getActiveRecommenders(round);

  const funder = findSettlementFunder(round);
  if (!funder) {
    console.warn(
      "No speculation grant funder found, skipping utility calculation",
    );
    return;
  }

  const recommenderBudgets = getUnilateralRecommenderBudgets(
    round,
    funder,
    recommenders,
  );

  let totalUtility = 0;

  // Match grantors of past speculation grants to users. Output a list of
  // matched and unmatched users.
  const usersById = keyBy(users, (u) => u.id);
  const pastSpeculationGrantorFullNames = new Set<string>();
  Object.values(grantData).map(({ simultaneousGrants }) => {
    for (const simultaneousGrant of simultaneousGrants) {
      for (const grant of simultaneousGrant.grants) {
        const speculationGrantorName = grant["Speculation Grantor"];
        if (speculationGrantorName) {
          pastSpeculationGrantorFullNames.add("" + speculationGrantorName);
        }
      }
    }
  });
  let speculationGrantorsMatched: string[] = [];
  let speculationGrantorsNotMatched: string[] = [];
  for (const pastSpeculationGrantorFullName of pastSpeculationGrantorFullNames.values()) {
    if (users.find((u) => u.name === pastSpeculationGrantorFullName)) {
      speculationGrantorsMatched.push(pastSpeculationGrantorFullName);
    } else {
      speculationGrantorsNotMatched.push(pastSpeculationGrantorFullName);
      console.log(
        `Evaluations by ${pastSpeculationGrantorFullName} will not be counted when evaluating some speculative grants`,
      );
      console.log(
        `${pastSpeculationGrantorFullName} made speculative grants, and is not an evaluator in this round`,
      );
    }
  }
  console.log(
    `The following users made speculation grants, and their evaluations will not be counted when evaluating how well those speculation grants did: ${speculationGrantorsMatched.join(", ")}`,
  );
  console.log(
    `The following users made speculation grants, but were not matched to any users in the current round: ${speculationGrantorsNotMatched.join(", ")}`,
  );

  const trancheListsByRecommender = Object.fromEntries(
    recommenders.map((recommender) => [
      recommender.id,
      getEvaluatorTrancheList(round, recommender),
    ]),
  );

  Object.values(grantData).map(({ application, simultaneousGrants }) => {
    let cumulativeGrantSum = 0;
    const recommendersForThisApplication = recommenders.filter(
      (recommender) => {
        return ["evaluated", "deferred"].includes(
          recommender.evaluations[application.id]?.state ?? "",
        );
      },
    );

    const recommenderBudgetsForThisApp = recommendersForThisApplication.map(
      (recommender) => recommenderBudgets[recommender.id],
    );

    const trancheLists = recommendersForThisApplication.map((recommender) => {
      let bottom = 0;

      const trancheList = trancheListsByRecommender[recommender.id];
      const result: Tranche[] = [];
      for (const tranche of trancheList.toReversed()) {
        const { height, allocations } = tranche;
        const tdv = bottom + height;
        bottom = tdv;
        result.push({
          tdv,
          bdv: bottom,
          size: allocations[application.id]?.amount ?? 0,
        });
      }
      result.reverse();
      return result;
    });

    for (const multiGrant of simultaneousGrants) {
      const parallelGrants = multiGrant.grants.map((grantRow) =>
        convertGrantAmountToNumber(grantRow["Speculator Amount"]),
      );
      const totalMultiGrantAmount = sum(parallelGrants);

      // Calculate utility for each grant using their share of total utility
      multiGrant.grants.forEach((grantRow) => {
        const speculationGrantorName = grantRow["Speculation Grantor"];

        // Get non-recused recommenders and their tranches/budgets for this grant
        const nonRecusedRecommenderIndices: number[] = [];
        const trancheListsForThisGrant = trancheLists.filter((tranche, idx) => {
          if (!speculationGrantorName) {
            nonRecusedRecommenderIndices.push(idx);
            return true;
          }

          const memberId = recommendersForThisApplication[idx].memberId;
          const userId = round.members[memberId].userId;
          const user = usersById[userId];

          if (!(user?.name && user.name === speculationGrantorName)) {
            nonRecusedRecommenderIndices.push(idx);
            return true;
          }

          return false;
        });

        // Get budgets for only the non-recused recommenders
        const budgetsForThisGrant = nonRecusedRecommenderIndices.map(
          (idx) => recommenderBudgetsForThisApp[idx],
        );

        // Calculate total utility of the full multigrant amount using appropriate recommenders
        const totalUtilityWithTheseRecommenders =
          weightedUtility(
            trancheListsForThisGrant,
            budgetsForThisGrant,
            cumulativeGrantSum + totalMultiGrantAmount,
          ) -
          weightedUtility(
            trancheListsForThisGrant,
            budgetsForThisGrant,
            cumulativeGrantSum,
          );

        // This grant gets its proportional share based on its amount
        const grantAmount = convertGrantAmountToNumber(
          grantRow["Speculator Amount"],
        );

        const scaleFactor =
          totalMultiGrantAmount > 0
            ? totalUtilityWithTheseRecommenders / totalMultiGrantAmount
            : 0;

        grantRow.utility = grantAmount * scaleFactor;

        totalUtility += grantRow.utility;
      });

      // Move to next multigrant
      cumulativeGrantSum += totalMultiGrantAmount;
    }
  });

  for (const { simultaneousGrants } of Object.values(grantData)) {
    for (const multiGrant of simultaneousGrants) {
      for (const grantRow of multiGrant.grants) {
        if (totalUtility !== 0) {
          grantRow.utility! /= totalUtility;
        } else {
          grantRow.utility = 0;
        }
      }
    }
  }
}

function getUnilateralRecommenderBudgets(
  round: RoundAggregate,
  funder: Funder,
  recommenders: Recommender[],
): { [recommenderId: string]: number } {
  const recommenderIds = recommenders.map((recommender) => recommender.id);
  const recommenderIndicesMap = getEvaluableIndicesMap(
    recommenderIds,
    round.holdProposalId,
  );
  const activeEvaluationIds = new Set(getEvaluatorActiveEvaluationIds(funder));

  const funderPreferenceList = trancheListToAlgoTranches(
    funder.trancheList,
    recommenderIndicesMap,
    {},
    activeEvaluationIds,
  );

  const recommenderBudgetsArray = funderPreferenceList.getRow(
    funderPreferenceList.n_rows - 1,
  );

  const recommenderBudgets: { [recommenderId: string]: number } = {};

  for (const [recommenderId, index] of Object.entries(recommenderIndicesMap)) {
    recommenderBudgets[recommenderId] = recommenderBudgetsArray[index];
  }

  return recommenderBudgets;
}

export function convertGrantAmountToNumber(
  grantSize: string | number | undefined,
): number {
  if (typeof grantSize === "number") return grantSize;
  if (typeof grantSize === "string") {
    const cleaned = grantSize.replace(/[$,]/g, "");
    const number = Number(cleaned);
    if (!isNaN(number)) return number;
  }
  throw new Error("Invalid grant size");
}

function processGrantData(
  grants: { [fieldName: string]: string }[],
  requests: { [fieldName: string]: string }[],
  users: UserProfile[],
  round: RoundAggregate,
): ApplicationSpeculationGrantData {
  const grantsAndApplications: ApplicationSpeculationGrantData = {};
  const proposalForms: ProposalForm[] = getActiveProposalForms(round);

  for (const grant of grants) {
    const sffApplicationId = grant["SFF ApplicationID (8 chars)"];
    const specGrantId = grant["Speculation Grant ID (same for multi-sigs)"];

    if (!grantsAndApplications[sffApplicationId]) {
      let matchingProposal: Proposal | null = null;
      for (const proposalForm of proposalForms) {
        for (const proposal of Object.values(proposalForm.proposals)) {
          const applicationIdFieldResponse =
            proposal.typedFields.sffApplicationId;

          if (applicationIdFieldResponse === sffApplicationId) {
            matchingProposal = proposal;
            break;
          }
        }

        if (matchingProposal) {
          break;
        }
      }

      if (!matchingProposal) {
        continue;
      }

      grantsAndApplications[sffApplicationId] = {
        application: matchingProposal,
        simultaneousGrants: [],
        // TODO: error if we can't find request
        // @ts-ignore
        request: null,
      };
    }

    if (!grantsAndApplications[sffApplicationId]) {
      continue;
    }

    let multiGrant = grantsAndApplications[
      sffApplicationId
    ].simultaneousGrants.find(
      (multiGrant) => multiGrant.specGrantId === specGrantId,
    );
    if (multiGrant == null) {
      multiGrant = {
        specGrantId,
        timestamp: grant["Request Timestamp"],
        grants: [],
      };
      grantsAndApplications[sffApplicationId].simultaneousGrants.push(
        multiGrant,
      );
    }

    multiGrant.grants.push(grant);

    if (grantsAndApplications[sffApplicationId].request) {
      continue;
    }
    const matchingRequest = requests.find(
      (request) => request["Timestamp"] === grant["Request Timestamp"],
    );

    if (matchingRequest) {
      grantsAndApplications[sffApplicationId].request = matchingRequest;
    }
  }

  // Sort all simultaneous grants by timestamp (earlier first)
  for (const grantData of Object.values(grantsAndApplications)) {
    grantData.simultaneousGrants.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  }

  addNormalizedWeightedUtilitiesToSpeculationGrantData(
    round,
    users,
    grantsAndApplications,
  );
  return grantsAndApplications;
}

function findSettlementFunder(round: RoundAggregate): Funder | null {
  const funders = getActiveFunders(round);

  if (funders.length === 0) {
    return null;
  }

  // Speculation Grants are a Jaan Tallinn initiative, so we use his funder
  // Better would be to add a property to the funder object to indicate that they're the speculation grant funder
  const jaanFunder = funders.find(
    (funder) =>
      funder.shortname === "Jaan Tallinn" || funder.shortname === "JT",
  );
  return jaanFunder || null;
}

function calculateTotalGrantAmount(
  grantsAndApplications: ApplicationSpeculationGrantData,
) {
  let totalGrantAmount = 0;
  for (const { simultaneousGrants } of Object.values(grantsAndApplications)) {
    for (const multiGrant of simultaneousGrants) {
      for (const grantRow of multiGrant.grants) {
        const amount = convertGrantAmountToNumber(
          grantRow["Speculator Amount"],
        );
        totalGrantAmount += amount;
      }
    }
  }
  return totalGrantAmount;
}
